import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, EventNote } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { batchesAPI } from '../../services/batches';
import { allocationsAPI } from '../../services/api';
import TeacherSessionPanel from '../../components/Teacher/TeacherSessionPanel';
import BatchPausePanel from '../../components/BatchPausePanel';
import BatchAssignedStudentsPanel from '../../components/BatchAssignedStudentsPanel';
import SessionClassPhotos from '../../components/Teacher/SessionClassPhotos';
import { useAuth } from '../../contexts/AuthContext';
import { getTodayYMDInTimezone } from '../../utils/dateTimeUtils';
import { getTimezoneOptionLabel } from '../../utils/timezoneUtils';
import { unwrapBatchDetail, getBatchStudents } from '../../utils/batchDetail';
import { sessionKindLabel } from '../../utils/sessionKindDisplay';

function normTime(t) {
  if (!t) return '';
  const s = String(t).trim().slice(0, 8);
  return s.length === 5 ? `${s}:00` : s;
}

function findSessionForSlot(sessions, sessionDate, startTime, endTime) {
  if (!sessionDate || !startTime || !sessions?.length) return null;
  const date = String(sessionDate).slice(0, 10);
  const start = normTime(startTime);
  const end = endTime ? normTime(endTime) : null;
  const exact =
    sessions.find((s) => {
      if (String(s.session_date).slice(0, 10) !== date) return false;
      if (normTime(s.start_time) !== start) return false;
      if (end && normTime(s.end_time) !== end) return false;
      return true;
    }) || null;
  if (exact) return exact;
  return (
    sessions.find(
      (s) => String(s.session_date).slice(0, 10) === date && String(s.session_kind || '') === 'merged'
    ) || null
  );
}

function pickDefaultSession(sessions = [], timezone) {
  const today = getTodayYMDInTimezone(timezone);
  const dated = (sessions || []).filter((s) => s?.session_date);
  const upcoming = dated
    .filter((s) => String(s.session_date).slice(0, 10) >= today)
    .sort(
      (a, b) =>
        String(a.session_date).localeCompare(String(b.session_date)) ||
        String(a.start_time || '').localeCompare(String(b.start_time || ''))
    );
  if (upcoming[0]) return upcoming[0];
  return [...dated].sort(
    (a, b) =>
      String(b.session_date).localeCompare(String(a.session_date)) ||
      String(b.start_time || '').localeCompare(String(b.start_time || ''))
  )[0] || null;
}

function sessionsOnDate(sessions = [], ymd) {
  if (!ymd) return [];
  const date = String(ymd).slice(0, 10);
  return (sessions || [])
    .filter((s) => String(s.session_date || '').slice(0, 10) === date)
    .sort((a, b) => String(a.start_time || '').localeCompare(String(b.start_time || '')));
}

export default function BatchDetailsPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { canManageBatches } = useAuth();
  const [unassigningAssignmentId, setUnassigningAssignmentId] = useState(null);
  const search = new URLSearchParams(location.search || '');
  const sessionIdFromQuery = search.get('sessionId');
  const sessionDateFromQuery = search.get('sessionDate');
  const startTimeFromQuery = search.get('startTime');
  const endTimeFromQuery = search.get('endTime');
  const actionFromQuery = search.get('action');

  const [viewDate, setViewDate] = useState(() => sessionDateFromQuery || '');

  useEffect(() => {
    if (sessionDateFromQuery) setViewDate(sessionDateFromQuery);
  }, [sessionDateFromQuery]);

  const batchQuery = useQuery(
    ['batch', batchId, viewDate || null],
    () => batchesAPI.getById(batchId, viewDate ? { sessionDate: viewDate } : {}),
    {
      enabled: !!batchId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const batch = unwrapBatchDetail(batchQuery.data);
  const defaultSession = useMemo(
    () => pickDefaultSession(batch?.sessions || [], batch?.teacher_timezone),
    [batch?.sessions, batch?.teacher_timezone]
  );
  const daySessions = useMemo(
    () => sessionsOnDate(batch?.sessions || [], viewDate),
    [batch?.sessions, viewDate]
  );

  const handleUnassignFromBatch = async (assignmentId, payload) => {
    if (!assignmentId || !batchId) return;
    setUnassigningAssignmentId(assignmentId);
    try {
      const response = await allocationsAPI.unassignOnLeave(assignmentId, payload || {});
      toast.success(response?.data?.message || 'Student unassigned');
      queryClient.invalidateQueries(['batch', batchId]);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unassign student');
      throw error;
    } finally {
      setUnassigningAssignmentId(null);
    }
  };

  const sessionFromSlot = useMemo(
    () =>
      findSessionForSlot(
        batch?.sessions || [],
        sessionDateFromQuery || viewDate,
        startTimeFromQuery,
        endTimeFromQuery
      ),
    [batch?.sessions, sessionDateFromQuery, viewDate, startTimeFromQuery, endTimeFromQuery]
  );

  const [selectedSessionId, setSelectedSessionId] = useState(() => sessionIdFromQuery || '');

  useEffect(() => {
    const wantedDate = sessionDateFromQuery || viewDate || '';

    if (sessionIdFromQuery) {
      const match = (batch?.sessions || []).find((s) => String(s.id) === String(sessionIdFromQuery));
      const matchDate = match?.session_date ? String(match.session_date).slice(0, 10) : '';
      if (!wantedDate || !matchDate || matchDate === wantedDate) {
        setSelectedSessionId(sessionIdFromQuery);
        if (matchDate && !viewDate) setViewDate(matchDate);
        return;
      }
    }

    if (sessionFromSlot?.id) {
      setSelectedSessionId(sessionFromSlot.id);
      return;
    }
    if (wantedDate && daySessions.length) {
      setSelectedSessionId(String(daySessions[0].id));
    }
  }, [
    sessionIdFromQuery,
    sessionDateFromQuery,
    sessionFromSlot?.id,
    viewDate,
    daySessions,
    batch?.sessions,
  ]);

  useEffect(() => {
    if (selectedSessionId || sessionIdFromQuery || sessionDateFromQuery || viewDate) return;
    if (!defaultSession?.id) return;
    setSelectedSessionId(String(defaultSession.id));
    if (defaultSession.session_date) setViewDate(String(defaultSession.session_date).slice(0, 10));
  }, [defaultSession, selectedSessionId, sessionIdFromQuery, sessionDateFromQuery, viewDate]);

  const session = useMemo(() => {
    if (!selectedSessionId || !batch?.sessions?.length) return null;
    return batch.sessions.find((s) => String(s.id) === String(selectedSessionId)) || null;
  }, [batch?.sessions, selectedSessionId]);

  const studentsForDate = useMemo(() => {
    const all = getBatchStudents(batch);
    if (!viewDate) return all;
    return all.filter((s) => {
      const assigned = s.assignment_date ? String(s.assignment_date).slice(0, 10) : null;
      if (!assigned) return true;
      return assigned <= viewDate;
    });
  }, [batch, viewDate]);

  const syncUrlForDate = (ymd, sessionId) => {
    const params = new URLSearchParams(location.search || '');
    if (ymd) params.set('sessionDate', ymd);
    else params.delete('sessionDate');
    if (sessionId) params.set('sessionId', String(sessionId));
    else params.delete('sessionId');
    params.delete('startTime');
    params.delete('endTime');
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  };

  const handlePickDate = (value) => {
    const ymd = value ? value.format('YYYY-MM-DD') : '';
    setViewDate(ymd);
    setSelectedSessionId('');
    syncUrlForDate(ymd, '');
  };

  const handlePickSession = (sessionId) => {
    setSelectedSessionId(sessionId);
    const match = (batch?.sessions || []).find((s) => String(s.id) === String(sessionId));
    const ymd = match?.session_date ? String(match.session_date).slice(0, 10) : viewDate;
    if (ymd) setViewDate(ymd);
    syncUrlForDate(ymd || '', sessionId);
  };

  const batchStart = batch?.start_date ? dayjs(String(batch.start_date).slice(0, 10)) : null;
  const todayYmd = getTodayYMDInTimezone(batch?.teacher_timezone);
  const weekAhead = dayjs(todayYmd).add(7, 'day');
  const rawEnd = batch?.end_date ? dayjs(String(batch.end_date).slice(0, 10)) : null;
  const maxViewDate = rawEnd && rawEnd.isBefore(weekAhead, 'day') ? rawEnd : weekAhead;

  if (batchQuery.isLoading && !batch) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!batch) {
    const apiMessage =
      batchQuery.error?.response?.data?.message ||
      batchQuery.error?.message ||
      'Batch not found.';
    return <Alert severity="error">{apiMessage}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography variant="h5" fontWeight={800}>
          {batch.name}
        </Typography>
        {(batch.country || batch.timezone) && (
          <Typography variant="body2" color="text.secondary">
            {[batch.country, batch.timezone ? getTimezoneOptionLabel(batch.timezone) : ''].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {session?.session_date ? <Chip label={session.session_date} icon={<EventNote />} /> : null}
      </Stack>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <DatePicker
            label="View class date"
            value={viewDate ? dayjs(viewDate) : null}
            onChange={handlePickDate}
            minDate={batchStart || undefined}
            maxDate={maxViewDate}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
          />
          {daySessions.length > 1 ? (
            <TextField
              select
              size="small"
              label="Class time"
              value={selectedSessionId || ''}
              onChange={(e) => handlePickSession(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {daySessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {String(s.start_time || '').slice(0, 5)}
                  {s.end_time ? ` – ${String(s.end_time).slice(0, 5)}` : ''}
                  {sessionKindLabel(s) ? ` (${sessionKindLabel(s)})` : ''}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {batchQuery.isFetching ? <CircularProgress size={20} /> : null}
        </Stack>
      </LocalizationProvider>

      {canManageBatches() && batch.teacherAssignmentAck && batch.teacherAssignmentAck.status !== 'not_applicable' && (
        <Alert
          severity={batch.teacherAssignmentAck.status === 'acknowledged' ? 'success' : 'warning'}
          sx={{ mb: 2 }}
        >
          {batch.teacherAssignmentAck.status === 'acknowledged'
            ? `Teacher has seen this batch assignment${batch.teacherAssignmentAck.readAt ? ` (${new Date(batch.teacherAssignmentAck.readAt).toLocaleString()})` : ''}.`
            : 'Teacher has not yet opened this batch assignment notification.'}
        </Alert>
      )}

      {selectedSessionId ? (
        <Box id="session-panel" sx={{ mb: 2 }}>
          <SessionClassPhotos sessionId={selectedSessionId} />
          <TeacherSessionPanel
            sessionId={selectedSessionId}
            autoOpenAction={actionFromQuery}
            batchStudents={studentsForDate}
          />
        </Box>
      ) : viewDate ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No class session found for {viewDate}. That date may not have a scheduled class, or it is more
          than 7 days ahead (sessions for cover/merge are created up to one week out).
        </Alert>
      ) : sessionDateFromQuery ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Class on {sessionDateFromQuery}
          {startTimeFromQuery ? ` at ${String(startTimeFromQuery).slice(0, 5)}` : ''} is scheduled. An
          attendance session will be created automatically when the class is due; you can also open this
          batch again after logging in.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a class date above to view attendance, class log, and homework for that day.
        </Alert>
      )}

      <BatchPausePanel batchId={batchId} activePause={batch.active_pause} />

      <Divider sx={{ my: 3 }} />
      <BatchAssignedStudentsPanel
        batch={batch}
        students={studentsForDate}
        canManageAssignments={canManageBatches()}
        onUnassign={handleUnassignFromBatch}
        unassigningAssignmentId={unassigningAssignmentId}
        title={viewDate ? `Assigned Students (as of ${viewDate})` : 'Assigned Students'}
      />
    </Box>
  );
}
